/**
 * UNIFIED AUTHENTICATION HOOK
 * 
 * This hook provides a unified interface for authentication that works with both:
 * 1. Auth0 (traditional OAuth login/signup)
 * 2. Custom JWT (seamless post-purchase authentication)
 * 
 * AUTHENTICATION METHODS:
 * 
 * AUTH0 FLOW:
 * - User clicks login/signup button
 * - Redirected to Auth0 for authentication
 * - Returns with Auth0 access token
 * - Token used for all API requests
 * - Standard OAuth flow with refresh tokens
 * 
 * CUSTOM JWT FLOW (Post-Purchase):
 * - User completes Stripe payment
 * - Backend generates custom JWT with 15-minute expiry
 * - Frontend receives auto-login URL with JWT
 * - JWT automatically processed and user logged in
 * - No Auth0 interaction required
 * - Immediate access to protected routes and API
 * 
 * API TOKEN MANAGEMENT:
 * - For Auth0 users: Automatically retrieves and refreshes Auth0 tokens
 * - For JWT users: Uses custom JWT token directly
 * - Backend validateToken middleware handles both token types
 * - Seamless switching between auth methods
 * 
 * USER ROLES AND PERMISSIONS:
 * - Admin status determined by backend user profile
 * - Payment status fetched from separate PaymentStatusContext
 * - Role-based access control for admin features
 * 
 * ERROR HANDLING:
 * - Automatic token refresh for Auth0
 * - Expired JWT detection and cleanup
 * - Graceful fallback to login prompts
 * - Comprehensive error logging
 */

import { useState, useEffect, useCallback } from 'react';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import { safeGetStorageItem } from '../utils/browserStorage';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import getApiBaseUrl from '../utils/apiBaseUrl';
import readJsonResponse from '../utils/readJsonResponse';

const inFlightProfileRequests = new Map();

const getSharedProfileRequest = (profileKey, requestFactory) => {
  const existingRequest = inFlightProfileRequests.get(profileKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = Promise.resolve().then(requestFactory);
  inFlightProfileRequests.set(profileKey, request);
  const clearRequest = () => {
    if (inFlightProfileRequests.get(profileKey) === request) {
      inFlightProfileRequests.delete(profileKey);
    }
  };
  request.then(clearRequest, clearRequest);
  return request;
};

const useUser = () => {
  // Get unified authentication state
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    authMethod,
    loginWithRedirect,
    logout,
    handleCustomJwtAuth,
    getApiToken,
    getFreshLoginIdToken
  } = useUnifiedAuth();

  // Get payment status from separate context
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
    error: paymentError,
    requiresSessionRefresh
  } = usePaymentStatus();

  // Local state for user profile data
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBetaTester, setIsBetaTester] = useState(false);

  // Extract stable user identifiers to prevent re-render cascades
  // Using individual fields instead of entire user object maintains stable dependencies
  const userId = user?.id;
  const userEmail = user?.email;
  const userSub = user?.sub || user?.auth0Sub;

  const buildFallbackProfile = useCallback(() => ({
    email: userEmail || null,
    id: userId || userSub,
    role: 'user',
    hasCompletedOnboarding: false,
    authMethod: authMethod
  }), [userEmail, userId, userSub, authMethod]);

  /**
   * FETCH USER PROFILE FROM BACKEND
   * Gets additional user data including admin status and profile details
   * Dependencies are individual user fields to prevent unnecessary re-fetches
   */
  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated || (!userId && !userSub)) {
      console.debug('[useUser] Not authenticated, skipping profile fetch');
      return;
    }

    try {
      setProfileLoading(true);
      const profileKey = `${authMethod || 'unknown'}:${userId || userSub || userEmail}`;
      const profileData = await getSharedProfileRequest(profileKey, async () => {
        // Backend identifies the user from the token; email is not required.
        console.debug('[useUser] Fetching user profile for user:', {
          email: userEmail || 'undefined',
          id: userId || 'undefined',
          auth0Sub: userSub || 'undefined',
          authMethod: authMethod
        });

        const token = await getApiToken();
        if (!token) {
          console.warn('[useUser] No API token available for profile fetch');
          return buildFallbackProfile();
        }

        const response = await fetchWithTimeout(`${getApiBaseUrl()}/users/user_profile`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('[useUser] Failed to fetch user profile:', response.status);
          return buildFallbackProfile();
        }

        const responseData = await readJsonResponse(response, { errorMessage: 'Failed to fetch user profile' });
        const resolvedProfile = responseData.profile || responseData;
        console.debug('[useUser] User profile fetched successfully:', {
          email: resolvedProfile.email,
          firstName: resolvedProfile.firstName,
          lastName: resolvedProfile.lastName,
          phone: resolvedProfile.phone,
          role: resolvedProfile.role,
          hasCompletedOnboarding: resolvedProfile.hasCompletedOnboarding,
          authMethod: authMethod,
          fullProfileData: resolvedProfile
        });
        return resolvedProfile;
      });

      setUserProfile(profileData);
      setIsAdmin(profileData.role === 'admin');
      setIsBetaTester(profileData.role === 'beta_tester');
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('[useUser] User profile fetch timed out; using fallback profile');
      } else {
        console.error('[useUser] Error fetching user profile:', error);
      }

      // Create fallback profile on error
      setUserProfile(buildFallbackProfile());
      setIsAdmin(false);
      setIsBetaTester(false);
    } finally {
      setProfileLoading(false);
    }
  }, [isAuthenticated, userId, userEmail, userSub, authMethod, getApiToken, buildFallbackProfile]);

  /**
   * FETCH PROFILE WHEN AUTHENTICATION STATE CHANGES
   * Automatically loads user profile when user logs in or auth method changes
   * Uses stable user identifiers to prevent unnecessary re-fetches
   */
  useEffect(() => {
    if (isAuthenticated && (userId || userSub) && !authLoading) {
      console.debug('[useUser] Authentication state changed, fetching profile');
      fetchUserProfile();
    } else if (!isAuthenticated) {
      // Clear profile data when user logs out
      console.debug('[useUser] User logged out, clearing profile data');
      setUserProfile(null);
      setIsAdmin(false);
      setIsBetaTester(false);
    }
  }, [isAuthenticated, userId, userSub, authLoading, fetchUserProfile]);

  /**
   * AUTH0 ACCESS TOKEN RETRIEVAL (Legacy Compatibility)
   * For backwards compatibility with components that still use getAccessTokenSilently
   */
  const getAccessTokenSilently = useCallback(async () => {
    console.debug('[useUser] getAccessTokenSilently called, auth method:', authMethod);
    
    if (authMethod === 'auth0') {
      // For Auth0 users, get the token through unified auth
      return await getApiToken();
    } else if (authMethod === 'custom_jwt') {
      // For JWT users, return the JWT token
      return await getApiToken();
    } else {
      console.warn('[useUser] No authentication method available for token retrieval');
      return null;
    }
  }, [authMethod, getApiToken]);

  /**
   * COMPREHENSIVE LOGGING FOR DEBUGGING
   * Tracks authentication state changes for troubleshooting
   * Uses stable userEmail to prevent excessive logging
   */
  useEffect(() => {
    console.debug('[useUser] Authentication state summary:', {
      isAuthenticated,
      authMethod,
      userEmail,
      hasProfile: !!userProfile,
      isAdmin,
      isBetaTester,
      isPayingCustomer,
      paymentError,
      requiresSessionRefresh,
      loading: {
        auth: authLoading,
        profile: profileLoading,
        payment: paymentLoading
      }
    });
  }, [isAuthenticated, authMethod, userEmail, userProfile, isAdmin, isBetaTester, isPayingCustomer, paymentError, requiresSessionRefresh, authLoading, profileLoading, paymentLoading]);

  // Calculate overall loading state
  const isLoading = authLoading || profileLoading;

  // Return unified authentication interface
  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    user: userProfile || user, // Prefer detailed profile over basic auth user
    
    // Authentication methods
    authMethod, // 'auth0' | 'custom_jwt' | null
    
    // Authentication actions
    loginWithRedirect,
    logout,
    handleCustomJwtAuth,
    
    // Token management
    getAccessTokenSilently, // Legacy compatibility
    getApiToken, // New unified token method
    getFreshLoginIdToken,
    
    // User profile data
    userProfile,
    isAdmin,
  isBetaTester,
    
    // Payment status (from separate context)
    isPayingCustomer,
    hasProSubscription: isPayingCustomer, // Alias for compatibility with AIBootcamp component
    paymentError,
    requiresSessionRefresh,
    
    // Profile management
    refreshProfile: fetchUserProfile,
    
    // Loading states
    authLoading,
    profileLoading,
    paymentLoading,
    
    // Raw authentication data for debugging
    rawAuthUser: user,
    debugInfo: {
      authMethod,
      hasStoredJwt: !!safeGetStorageItem('localStorage', 'customAuthToken'),
      userAgent: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'
    }
  };
};

export default useUser;
